import React, { useState, useMemo, useEffect, useRef } from 'react';
import { bulkUpdatePrices } from '../api';

// Bulk reprice for the rows a pricer selected on Compare: adjust by $, adjust
// by %, or set a price. Every listing previews as current → new; the checked
// ones go out in one bulk-price call (≤50 per click). Per-row results render
// in place and the modal stays open so failures can't be missed — same shape
// as AiBulkReviewModal.
//
// Props: listings (my inventory rows), suggestionsByInvId (Map, optional — a
//        pending AI suggestion id rides along so its accepted/modified
//        telemetry is recorded), onClose(), onApplied({ results, errors }),
//        onOutcomeUnknown() (optional — fired when the request timed out /
//        died mid-flight, so the parent can schedule fresh reads)
//
// Timeout safety: a 504/network failure does NOT mean the batch failed — the
// server keeps applying after the proxy gives up. Re-applying would cut
// prices twice. So on an unknown outcome the modal locks Apply, remembers
// each row's intended price, and reconciles against the live listings prop
// (the parent refetches): rows whose price landed are reported as applied,
// rows still at their old price after the recheck window unlock for a retry
// (the server's stale-price guard is the backstop).
const MODES = [
  { id: 'amount', label: 'Adjust by $' },
  { id: 'percent', label: 'Adjust by %' },
  { id: 'set', label: 'Set price' },
];

// Whole dollars: matches the table's step="1" inputs and Skybox's own bulk adjust
function computeNewPrice(listing, mode, direction, value) {
  const cur = Number(listing.listPrice) || 0;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  let next;
  if (mode === 'set') next = v;
  else if (mode === 'amount') next = cur + direction * v;
  else next = cur * (1 + (direction * v) / 100);
  return Math.round(next);
}

function BulkPriceModal({ listings, suggestionsByInvId, onClose, onApplied, onOutcomeUnknown }) {
  const [mode, setMode] = useState('amount');
  const [direction, setDirection] = useState(-1); // -1 lower, +1 raise
  const [value, setValue] = useState('');
  const [checked, setChecked] = useState(() => new Set(listings.map(l => String(l.id))));
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState({}); // id -> { ok, oldPrice, newPrice } | { ok: false, error }
  const [globalError, setGlobalError] = useState('');
  // Rows whose last apply timed out (outcome unknown): id -> { intendedPrice, priorPrice }
  const [pendingOutcome, setPendingOutcome] = useState({});
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;

  const rows = useMemo(() => listings.map(l => {
    const newPrice = value === '' ? null : computeNewPrice(l, mode, direction, value);
    const invalid = newPrice != null && newPrice < 0;
    const cur = Number(l.listPrice) || 0;
    const delta = newPrice != null && !invalid && cur > 0 ? ((newPrice - cur) / cur) * 100 : null;
    return { l, id: String(l.id), newPrice, invalid, delta };
  }), [listings, mode, direction, value]);

  const eligible = rows.filter(r => r.newPrice != null && !r.invalid && !results[r.id]?.ok && !pendingOutcome[r.id]);
  const selected = eligible.filter(r => checked.has(r.id));
  const allChecked = eligible.length > 0 && eligible.every(r => checked.has(r.id));

  const toggle = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (allChecked) setChecked(new Set());
    else setChecked(new Set(eligible.map(r => r.id)));
  };

  const apply = async () => {
    if (!selected.length) return;
    const batch = selected.slice(0, 50);
    setApplying(true); setGlobalError('');
    try {
      const res = await bulkUpdatePrices(batch.map(r => ({
        inventoryId: r.l.id,
        newPrice: r.newPrice,
        suggestionId: suggestionsByInvId?.get(r.id)?.id,
        // Stale-price guard: the server rejects a row (409) if its price no
        // longer matches the Current B this batch was computed from — turns a
        // re-apply of an already-landed batch into per-row no-ops
        expectedCurrentPrice: Number(r.l.listPrice) || 0,
        // Hints for the server's L-price what-if (works without them too)
        vividListingId: r.l.vividListingId || undefined,
        zone: !!r.l.zone,
      })));
      const next = { ...results };
      for (const ok of res.data.results || []) next[String(ok.inventoryId)] = { ok: true, oldPrice: ok.oldPrice, newPrice: ok.newPrice };
      for (const bad of res.data.errors || []) next[String(bad.inventoryId)] = { ok: false, error: bad.error };
      setResults(next);
      onApplied?.(res.data);
      if (selected.length > 50) setGlobalError(`Applied the first 50; ${selected.length - 50} remain selected — click Apply again.`);
    } catch (err) {
      const status = err.response?.status;
      const outcomeUnknown = status === 504 || status === 502 || err.code === 'ECONNABORTED' || !err.response;
      if (outcomeUnknown) {
        // The server may still be applying this batch — lock these rows until
        // a fresh read shows what actually happened.
        const pending = {};
        for (const r of batch) pending[r.id] = { intendedPrice: r.newPrice, priorPrice: Number(r.l.listPrice) || 0 };
        setPendingOutcome(pending);
        setGlobalError('Timed out waiting for the server — it may still be applying these prices. Do NOT re-apply; checking actual prices…');
        onOutcomeUnknown?.();
      } else {
        setGlobalError(err.response?.data?.error || err.message || 'Bulk update failed');
      }
    } finally {
      setApplying(false);
    }
  };

  // Reconcile unknown-outcome rows against the live listings prop (the parent
  // refetches after onOutcomeUnknown): a row now at its intended price DID
  // apply — lock it and report it upstream like a normal success.
  useEffect(() => {
    const pendingIds = Object.keys(pendingOutcome);
    if (!pendingIds.length) return;
    const liveById = new Map(listings.map(l => [String(l.id), l]));
    const reconciled = [];
    for (const id of pendingIds) {
      const live = liveById.get(id);
      if (live && Math.abs((Number(live.listPrice) || 0) - pendingOutcome[id].intendedPrice) < 0.01) {
        reconciled.push({ inventoryId: live.id, oldPrice: pendingOutcome[id].priorPrice, newPrice: pendingOutcome[id].intendedPrice, unitPrice: null, success: true });
      }
    }
    if (!reconciled.length) return;
    setResults(prev => {
      const next = { ...prev };
      for (const r of reconciled) next[String(r.inventoryId)] = { ok: true, oldPrice: r.oldPrice, newPrice: r.newPrice };
      return next;
    });
    const remaining = { ...pendingOutcome };
    for (const r of reconciled) delete remaining[String(r.inventoryId)];
    setPendingOutcome(remaining);
    if (!Object.keys(remaining).length) {
      setGlobalError('The timed-out batch was applied by the server — those rows are confirmed. Nothing to re-apply.');
    }
    onAppliedRef.current?.({ results: reconciled, errors: [] });
  }, [listings, pendingOutcome]);

  // Recheck window: rows still at their old price ~60 s after the timeout
  // genuinely didn't apply — unlock them for a retry (the server's 409 guard
  // covers the race where the write lands even later).
  useEffect(() => {
    if (!Object.keys(pendingOutcome).length) return;
    const t = setTimeout(() => {
      setPendingOutcome({});
      setGlobalError('Rows still showing their old price were not applied — you can re-apply them.');
    }, 60000);
    return () => clearTimeout(t);
  }, [pendingOutcome]);
  const pendingCount = Object.keys(pendingOutcome).length;

  const fmt = (v) => v == null ? '-' : `$${Number(v).toFixed(2)}`;
  const totalDelta = selected.reduce((s, r) => s + ((r.newPrice - (Number(r.l.listPrice) || 0)) * (r.l.quantity || 1)), 0);
  const invalidCount = rows.filter(r => r.invalid).length;
  const amountLabel = mode === 'set' ? 'New price $' : mode === 'amount' ? 'Amount $' : 'Percent %';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-content" style={{ maxWidth: 900, width: '95%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Bulk price update ({listings.length})</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <div className="bulk-price-controls">
            <div className="bulk-price-modes">
              {MODES.map(m => (
                <button
                  key={m.id}
                  type="button"
                  className={`bulk-mode-btn${mode === m.id ? ' active' : ''}`}
                  onClick={() => setMode(m.id)}
                  disabled={applying}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {mode !== 'set' && (
              <div className="bulk-price-dir">
                <button type="button" className={`bulk-mode-btn${direction === 1 ? ' active' : ''}`} onClick={() => setDirection(1)} disabled={applying}>+ Raise</button>
                <button type="button" className={`bulk-mode-btn${direction === -1 ? ' active' : ''}`} onClick={() => setDirection(-1)} disabled={applying}>− Lower</button>
              </div>
            )}
            <label className="bulk-price-amount">
              <span>{amountLabel}</span>
              <input
                type="number"
                min="0"
                step={mode === 'percent' ? '0.5' : '1'}
                value={value}
                onChange={e => setValue(e.target.value)}
                disabled={applying}
                autoFocus
              />
            </label>
            <span className="ai-admin-note">Rounded to whole dollars.</span>
          </div>

          <table className="bulk-review-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ width: 28 }}><input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={!eligible.length || applying} /></th>
                <th>Section</th><th>Row</th><th>Seats</th><th>Qty</th><th>Current B</th><th>New B</th><th>Δ</th><th>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ l, id, newPrice, invalid, delta }) => {
                const r = results[id];
                const isPending = !!pendingOutcome[id];
                const locked = !!r?.ok || invalid || newPrice == null || isPending;
                return (
                  <tr key={id} style={{ opacity: r?.ok ? 0.7 : 1 }}>
                    <td><input type="checkbox" checked={checked.has(id) && !locked} disabled={locked || applying} onChange={() => toggle(id)} /></td>
                    <td>{l.displayName || l.section || '-'}</td>
                    <td>{l.row || '-'}</td>
                    <td>{l.lowSeat && l.highSeat ? `${l.lowSeat}-${l.highSeat}` : l.seatNumbers || '-'}</td>
                    <td>{l.quantity ?? '-'}</td>
                    <td>{fmt(l.listPrice)}</td>
                    <td style={{ fontWeight: 600 }}>
                      {newPrice == null ? '-' : invalid ? <span style={{ color: '#ff8a8a' }}>below $0</span> : fmt(newPrice)}
                    </td>
                    <td style={{ color: delta < 0 ? '#ff8a8a' : '#7ee787' }}>{delta == null ? '-' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}</td>
                    <td>
                      {r?.ok && <span style={{ color: '#7ee787' }}>✓ {fmt(r.oldPrice)} → {fmt(r.newPrice)}</span>}
                      {!r?.ok && isPending && <span style={{ color: '#e3b341' }}>confirming…</span>}
                      {r && !r.ok && !isPending && <span style={{ color: '#ff8a8a' }}>{r.error}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {invalidCount > 0 && (
            <div className="ai-inline-error" style={{ marginTop: 10 }}>
              {invalidCount} listing{invalidCount === 1 ? '' : 's'} would go below $0 and will be skipped.
            </div>
          )}
          {globalError && <div className="ai-inline-error" style={{ marginTop: 10 }}>{globalError}</div>}
        </div>
        <div className="modal-footer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: '1px solid var(--color-border)' }}>
          <span className="ai-admin-note">
            {selected.length} selected{selected.length ? ` · net ${totalDelta >= 0 ? '+' : '-'}$${Math.abs(totalDelta).toFixed(0)} across selected seats` : ''}
            {selected.length > 50 ? ' · applies 50 at a time' : ''}
          </span>
          <span style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={onClose} disabled={applying}>Close</button>
          <button className="btn-primary" onClick={apply} disabled={applying || !selected.length || pendingCount > 0}>
            {applying ? 'Applying…' : pendingCount > 0 ? 'Confirming…' : `Apply ${Math.min(selected.length, 50)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkPriceModal;
